type WorldPoint = [number, number, number];

/**
 * 预览气泡引线锚点输入。
 */
export interface PreviewBalloonLeaderInput {
    /** 来源选择项引用。 */
    sourceRef?: string | null;
    /** 局部坐标系下的引线起点。 */
    anchor: WorldPoint;
}

/**
 * 预览气泡构建输入。
 */
export interface PreviewBalloonGuideInput {
    /** 气泡唯一 ID。 */
    id: string;
    /** 来源实体对象 ID。 */
    sourceObjectId: string;
    /** 来源选择项引用。 */
    sourceRef?: string | null;
    /** 气泡定位基准点；缺省时使用引线锚点中心。 */
    anchor?: WorldPoint;
    /** 气泡外拉方向锚点；渲染层会按 anchor -> labelDirectionAnchor 的屏幕方向放置气泡。 */
    labelDirectionAnchor?: WorldPoint;
    /** 一组引线锚点；缺省时使用 anchor 生成单引线。 */
    leaderAnchors?: PreviewBalloonLeaderInput[];
    /** 屏幕偏移。 */
    offsetPx?: BlendPreviewGuideOffset;
    /** 标题。 */
    title?: string;
    /** 展示值。 */
    valueText?: string;
    /** 结构化字段；适用于 SolidWorks 风格多行气泡。 */
    fields?: BlendPreviewBalloonField[];
    /** 实体侧引线端点标记。 */
    anchorMarker?: 'none' | 'dot';
}

/**
 * 预览气泡构建器参数。
 */
export interface PreviewBalloonGuideBuilderOptions {
    /** 默认屏幕偏移。 */
    defaultOffsetPx?: BlendPreviewGuideOffset;
    /** 默认实体侧引线端点标记。 */
    defaultAnchorMarker?: 'none' | 'dot';
}

/**
 * 特征预览气泡构建器。
 *
 * 设计说明：
 * 1. 气泡引导是跨特征的展示语义，不属于某一个圆角或倒角类型；
 * 2. 本类只产出可序列化纯数据，不持有 Puppy CAD 内核、three 或 DOM 对象；
 * 3. 单锚点、多锚点、端点标记和多字段表格式气泡在这里统一收口。
 */
export class PreviewBalloonGuideBuilder {
    /** 默认屏幕偏移。 */
    private readonly defaultOffsetPx: BlendPreviewGuideOffset;

    /** 默认实体侧引线端点标记。 */
    private readonly defaultAnchorMarker: 'none' | 'dot';

    /**
     * 创建预览气泡构建器。
     *
     * @param options 构建器参数。
     */
    public constructor(options: PreviewBalloonGuideBuilderOptions = {}) {
        this.defaultOffsetPx = options.defaultOffsetPx ?? { x: 72, y: -48 };
        this.defaultAnchorMarker = options.defaultAnchorMarker ?? 'dot';
    }

    /**
     * 构建单个气泡引导。
     *
     * @param input 气泡输入。
     * @returns 可传递给渲染层的纯数据气泡引导。
     */
    public build(input: PreviewBalloonGuideInput): BlendPreviewBalloonGuide {
        const leaderAnchors = this.resolveLeaderAnchors(input);
        const anchor = input.anchor ?? this.computePointCloudCenter(
            leaderAnchors.map((leaderAnchor) => leaderAnchor.anchor),
        );
        const fields = this.normalizeFields(input.fields);

        return {
            kind: 'balloon',
            id: input.id,
            sourceObjectId: input.sourceObjectId,
            sourceRef: input.sourceRef ?? leaderAnchors[0]?.sourceRef ?? null,
            anchor,
            labelDirectionAnchor: input.labelDirectionAnchor,
            leaderAnchors,
            anchorMarker: input.anchorMarker ?? this.defaultAnchorMarker,
            offsetPx: input.offsetPx ?? this.defaultOffsetPx,
            title: input.title ?? '',
            valueText: input.valueText ?? this.formatFields(fields),
            fields,
        };
    }

    /**
     * 批量构建气泡引导。
     *
     * @param inputs 气泡输入集合。
     * @returns 气泡引导集合。
     */
    public buildMany(inputs: PreviewBalloonGuideInput[]): BlendPreviewBalloonGuide[] {
        return inputs.map((input) => this.build(input));
    }

    /**
     * 解析引线锚点集合。
     *
     * @param input 气泡输入。
     * @returns 至少包含一个锚点的引线集合。
     */
    private resolveLeaderAnchors(input: PreviewBalloonGuideInput): PreviewBalloonLeaderInput[] {
        if (Array.isArray(input.leaderAnchors) && input.leaderAnchors.length > 0) {
            return input.leaderAnchors.map((leaderAnchor) => ({
                sourceRef: leaderAnchor.sourceRef ?? null,
                anchor: leaderAnchor.anchor,
            }));
        }
        if (!input.anchor) {
            throw new Error(`预览气泡 ${input.id} 缺少 anchor 或 leaderAnchors`);
        }
        return [{
            sourceRef: input.sourceRef ?? null,
            anchor: input.anchor,
        }];
    }

    /**
     * 归一化结构化字段。
     *
     * @param fields 原始字段。
     * @returns 可序列化字段集合。
     */
    private normalizeFields(fields: BlendPreviewBalloonField[] | undefined): BlendPreviewBalloonField[] | undefined {
        if (!fields || fields.length === 0) {
            return undefined;
        }
        return fields.map((field) => ({
            label: field.label,
            valueText: field.valueText,
        }));
    }

    /**
     * 将结构化字段格式化为兼容旧渲染器的文本。
     *
     * @param fields 字段集合。
     * @returns 多行文本。
     */
    private formatFields(fields: BlendPreviewBalloonField[] | undefined): string {
        if (!fields || fields.length === 0) {
            return '';
        }
        return fields.map((field) => `${field.label} ${field.valueText}`).join('\n');
    }

    /**
     * 计算点集合中心。
     *
     * @param points 点集合。
     * @returns 点云中心。
     */
    private computePointCloudCenter(points: WorldPoint[]): WorldPoint {
        if (points.length === 0) {
            throw new Error('预览气泡缺少可计算中心的引线锚点');
        }
        const sum = points.reduce<WorldPoint>((accumulator, point) => [
            accumulator[0] + point[0],
            accumulator[1] + point[1],
            accumulator[2] + point[2],
        ], [0, 0, 0]);
        return [
            sum[0] / points.length,
            sum[1] / points.length,
            sum[2] / points.length,
        ];
    }
}

